import { useCallback, useEffect, useRef, useState } from 'react';
import { translate, type WashedLocale } from '@washed/i18n';
import { rememberLocalePreference } from '../lib/localePreference.js';
import { scrollToWaitlist } from '../lib/scrollToWaitlist.js';
import { localeCounterpartPath } from '../routes/localeRoutes.js';
import styles from './MarketingNav.module.css';

export interface MarketingNavProps {
  locale: WashedLocale;
  onLocaleChange: (next: WashedLocale) => void;
  context?: 'home' | 'page';
}

export function MarketingNav({ locale, onLocaleChange, context = 'page' }: MarketingNavProps) {
  const t = (key: Parameters<typeof translate>[0]) => translate(key, locale);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const menuId = `marketing-nav-${context}-menu`;
  const homeHref = locale === 'en' ? '/en' : '/';
  const pricingHref = locale === 'en' ? '/en/tarifs' : '/tarifs';
  const zonesHref = locale === 'en' ? '/en/zones' : '/zones';
  const serviceHref =
    context === 'home'
      ? '#service'
      : locale === 'en'
        ? '/en/comment-ca-marche'
        : '/comment-ca-marche';
  const workerHref =
    context === 'home' ? '#worker' : locale === 'en' ? '/en/devenir-laveuse' : '/devenir-laveuse';
  const waitlistHref = locale === 'en' ? '/en#rejoindre' : '/#rejoindre';

  // Read after hydration: the prerender has no window, and the attribute must
  // not differ between server HTML and the first client render.
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  useEffect(() => {
    setCurrentPath(window.location.pathname.replace(/\/+$/u, '') || '/');
  }, []);
  const current = (href: string) => (href === currentPath ? ('page' as const) : undefined);

  useEffect(() => {
    if (context !== 'home') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let last = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y > last + 4 && y > 160) setHidden(true);
        else if (y < last - 4) setHidden(false);
        last = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [context]);

  const closeMenu = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeMenu, open]);

  useEffect(() => {
    if (open) firstLinkRef.current?.focus();
  }, [open]);

  const handleLocaleToggle = useCallback(() => {
    const nextLocale: WashedLocale = locale === 'fr' ? 'en' : 'fr';
    rememberLocalePreference(nextLocale);

    if (typeof window !== 'undefined') {
      const counterpart = localeCounterpartPath(window.location.pathname, locale);
      if (counterpart !== null) {
        window.location.assign(`${counterpart}${window.location.search}${window.location.hash}`);
        closeMenu();
        return;
      }
    }

    onLocaleChange(nextLocale);
    closeMenu();
  }, [closeMenu, locale, onLocaleChange]);

  const handleWaitlistClick = useCallback(() => {
    closeMenu();
    scrollToWaitlist();
  }, [closeMenu]);

  const wrapClass = [
    styles.wrap,
    context === 'home' ? styles.homeWrap : styles.pageWrap,
    hidden && !open ? styles.hidden : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <a href="#contenu" className={styles.skip}>
        {t('marketing.nav.skip')}
      </a>
      <div className={wrapClass}>
        <nav className={styles.nav} aria-label="Navigation">
          <a
            href={homeHref}
            className={styles.wordmark}
            aria-label={locale === 'fr' ? 'Washed, accueil' : 'Washed, home'}
          >
            washed<span className={styles.wordmarkDot}>.</span>
          </a>

          <button
            type="button"
            ref={menuButtonRef}
            className={styles.hamburger}
            aria-label="Menu"
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpen((value) => !value)}
          >
            <span />
            <span />
            <span />
          </button>

          <div
            id={menuId}
            className={[styles.links, open ? styles.menuOpen : ''].filter(Boolean).join(' ')}
          >
            {context === 'page' && (
              <a
                href={homeHref}
                ref={firstLinkRef}
                onClick={closeMenu}
                aria-current={current(homeHref)}
              >
                {locale === 'fr' ? 'Accueil' : 'Home'}
              </a>
            )}
            <a
              href={serviceHref}
              ref={context === 'home' ? firstLinkRef : undefined}
              onClick={closeMenu}
              aria-current={current(serviceHref)}
            >
              {t('marketing.nav.service')}
            </a>
            <a href={pricingHref} onClick={closeMenu} aria-current={current(pricingHref)}>
              {t('marketing.nav.tarif')}
            </a>
            <a href={zonesHref} onClick={closeMenu} aria-current={current(zonesHref)}>
              {t('marketing.nav.quartiers')}
            </a>
            <a href={workerHref} onClick={closeMenu} aria-current={current(workerHref)}>
              {t('marketing.nav.worker')}
            </a>

            <button
              type="button"
              className={styles.language}
              aria-label={t('marketing.nav.lang_aria')}
              onClick={handleLocaleToggle}
            >
              <span className={locale === 'fr' ? styles.activeLocale : undefined}>FR</span>
              <span className={styles.languageSeparator} aria-hidden="true">
                ·
              </span>
              <span className={locale === 'en' ? styles.activeLocale : undefined}>EN</span>
            </button>

            {context === 'home' ? (
              <button type="button" className={styles.cta} onClick={handleWaitlistClick}>
                {t('marketing.nav.waitlist')} <span aria-hidden="true">→</span>
              </button>
            ) : (
              <a className={styles.cta} href={waitlistHref} onClick={closeMenu}>
                {t('marketing.nav.waitlist')} <span aria-hidden="true">→</span>
              </a>
            )}
          </div>
        </nav>
      </div>
    </>
  );
}
